import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { useEffect, useState } from "react";
import { appendScript } from "../utils";
// Initialize custom event for wistia
if ("object" !== "undefined") {
    window._wq = window._wq || [];
    window._wq.push({
        id: "_all",
        onReady: function() {
            document.dispatchEvent(new Event("wistia_loaded"));
        }
    });
}
var classnames = {
    thumbnail: "wistia_thumbnail",
    responsiveWrapper: "wistia_responsive_wrapper",
    deferred: "wistia_deferred",
    embed: "wistia_embed",
    swatch: "wistia_swatch",
    background: "background",
    async: function(id) {
        return "wistia_async_".concat(id);
    }
};
var wistiaFlags = {
    noSEO: "seo=false",
    videoFoam: "videoFoam=true",
    autoPlay: "autoPlay=true"
};
// Hook to initialize the listeners/setup on wistia (managing classes to display the video), use useEffect to avoid SSR issues
var useWistia = function(videoId) {
    var addThumbnailsListeners = function() {
        var thumbnails = [].slice.call(document.getElementsByClassName(classnames.thumbnail));
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = thumbnails[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var thumbnail = _step.value;
                if (!thumbnail.dataset.hasListeners) {
                    thumbnail.dataset.hasListeners = "true";
                    thumbnail.addEventListener("click", handleThumbnailClick);
                    thumbnail.addEventListener("keypress", handleThumbnailKeyPress);
                }
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    };
    var handleThumbnailKeyPress = function(event) {
        event.preventDefault();
        if (event.key === " " || event.key === "Enter") {
            handleThumbnailClick(event);
        }
    };
    var handleThumbnailClick = function(event) {
        var ref;
        var thumbnail = event.target;
        var wrapper = (ref = thumbnail.parentNode) === null || ref === void 0 ? void 0 : ref.parentNode;
        if (!wrapper) {
            return;
        }
        var deferred = wrapper.querySelector(".".concat(classnames.deferred));
        if (deferred) {
            deferred.classList.add(classnames.responsiveWrapper);
            wrapper.style.minHeight = "".concat(thumbnail.offsetHeight, "px");
            thumbnail.classList.add(classnames.background);
            initWistiaVideo({
                wrapper: deferred
            });
        }
    };
    var initializeWrappers = function() {
        var wrappers = [].slice.call(document.getElementsByClassName(classnames.responsiveWrapper));
        if (wrappers.length === 0) {
            return;
        }
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = wrappers[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var wrapper = _step.value;
                initWistiaVideo({
                    wrapper: wrapper
                });
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    };
    useEffect(function() {
        if (videoId) {
            addThumbnailsListeners();
            initializeWrappers();
        }
    });
};
var initWistiaVideo = function(param) {
    var wrapper = param.wrapper, _embedId = param.embedId, embedId = _embedId === void 0 ? null : _embedId, _autoPlay = param.autoPlay, autoPlay = _autoPlay === void 0 ? false : _autoPlay, _videoId = param.videoId, videoId = _videoId === void 0 ? null : _videoId, _classes = param.classes, classes = _classes === void 0 ? [] : _classes;
    if (!wrapper || wrapper.dataset.wistiaPrepared) {
        return;
    }
    var wistiaId = wrapper.dataset.wistiaVideoId || videoId;
    if (wistiaId) {
        var _classList;
        appendWistiaScript();
        wrapper.dataset.wistiaPrepared = "true";
        var wrapperIsDeferred = wrapper.classList.contains(classnames.deferred);
        var embedClasses = [
            classnames.embed,
            classnames.async(wistiaId),
            wistiaFlags.noSEO,
            wistiaFlags.videoFoam, 
        ];
        if (classes.length) {
            var _embedClasses;
            (_embedClasses = embedClasses).push.apply(_embedClasses, _to_consumable_array(classes));
        }
        if (autoPlay || wrapperIsDeferred) {
            embedClasses.push(wistiaFlags.autoPlay);
        }
        var embed = document.createElement("div");
        (_classList = embed.classList).add.apply(_classList, _to_consumable_array(embedClasses));
        var swatch = document.createElement("div");
        swatch.classList.add(classnames.swatch);
        swatch.style.cssText = "height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;";
        wrapper.appendChild(embed).appendChild(swatch);
        wrapper.dataset.testid = "wistia-element-ref";
        if (embedId) {
            embed.id = embedId;
        }
    }
};
// Wistia hook so we can have easy access to "onReady" listener and get the video api
var useWistiaVideoApi = function(param) {
    var id = param.id, jsonp = param.jsonp, onReady = param.onReady, _options = param.options, options = _options === void 0 ? {} : _options;
    var ref = useState(), videoApi = ref[0], setVideoApi = ref[1];
    useEffect(function() {
        window._wq = window._wq || [];
        window._wq.push({
            id: id,
            onReady: function(api) {
                setVideoApi(api);
                if (jsonp) {
                    appendWistiaJsonpScript(id);
                }
                onReady && onReady(api);
                return api;
            },
            options: options
        });
    }, []);
    return videoApi;
};
var appendWistiaScript = function() {
    appendScript("https://fast.wistia.com/assets/external/E-v1.js");
};
var appendWistiaJsonpScript = function(id) {
    appendScript("https://fast.wistia.com/embed/medias/".concat(id, ".jsonp"));
};
export { initWistiaVideo, useWistia, useWistiaVideoApi, appendWistiaScript, appendWistiaJsonpScript };
